(() => {
  'use strict';

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const lerp = (from, to, progress) => from + (to - from) * progress;
  const smoothstep = (progress) => progress * progress * (3 - 2 * progress);
  const toNumber = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const parseColor = (value) => {
    const color = String(value || '').trim();
    const shortHex = /^#([\da-f])([\da-f])([\da-f])$/i.exec(color);
    if (shortHex) {
      return shortHex.slice(1).map((part) => Number.parseInt(part + part, 16));
    }

    const hex = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(color);
    if (hex) {
      return hex.slice(1).map((part) => Number.parseInt(part, 16));
    }

    const rgb = /^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i.exec(color);
    if (rgb) {
      return rgb.slice(1, 4).map((part) => clamp(Number.parseFloat(part), 0, 255));
    }

    return [32, 32, 32];
  };

  const mixColor = (from, to, progress) => {
    const first = parseColor(from);
    const second = parseColor(to);
    const channels = first.map((channel, index) => Math.round(lerp(channel, second[index], progress)));
    return `rgb(${channels.join(', ')})`;
  };

  class MonochromeScrollIntro extends HTMLElement {
    constructor() {
      super();
      this._frame = 0;
      this._currentProgress = 0;
      this._targetProgress = 0;
      this._onViewportChange = this._onViewportChange.bind(this);
      this._onResize = this._onResize.bind(this);
      this._tick = this._tick.bind(this);
      this._isMobile = false;
      this._videoPanelHeight = 0;
      this._heroHeight = 0;
      this._logoHeight = 0;
      // Posição atual (suavizada) da logo no mobile. null = ainda não inicializada,
      // então o primeiro quadro crava sem animar a partir do zero.
      this._logoY = null;
      // Cache do último valor escrito em cada custom property/classe, pra pular escritas
      // redundantes no DOM durante o scroll (ver _setProp/_toggleClass).
      this._props = {};
      this._classes = {};
      this._onMotionChange = this._onMotionChange.bind(this);
      this._updateHeaderOffset = this._updateHeaderOffset.bind(this);
      this._onIntroClick = this._onIntroClick.bind(this);
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      this._sticky = this.querySelector('.mm-intro__sticky');
      this._logoEl = this.querySelector('.mm-intro__logo');
      this._hero = this.querySelector('[data-intro-hero]');
      this._videoPanel = this.querySelector('[data-intro-video-panel]');
      this._videos = Array.from(this.querySelectorAll('video'));
      this._motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      window.addEventListener('scroll', this._onViewportChange, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      window.addEventListener('resize', this._updateHeaderOffset, { passive: true });
      window.addEventListener('load', this._updateHeaderOffset);
      this.addEventListener('click', this._onIntroClick);

      if (this._motionQuery.addEventListener) {
        this._motionQuery.addEventListener('change', this._onMotionChange);
      } else if (this._motionQuery.addListener) {
        this._motionQuery.addListener(this._onMotionChange);
      }

      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(this._onResize);
        this._resizeObserver.observe(this);
        if (this._videoPanel) this._resizeObserver.observe(this._videoPanel);
        if (this._hero) this._resizeObserver.observe(this._hero);
      }

      // Enquanto a seção está visível, mantemos o loop de animação rodando frame a
      // frame. Isso deixa o deslize da logo fluido mesmo no scroll por inércia do
      // mobile (onde os eventos de 'scroll' chegam espaçados). Fora da viewport, para.
      this._inView = true;
      if ('IntersectionObserver' in window) {
        this._selfObserver = new IntersectionObserver((entries) => {
          this._inView = entries.some((entry) => entry.isIntersecting);
          if (this._inView && !this._frame) this._frame = requestAnimationFrame(this._tick);
        }, { threshold: 0 });
        this._selfObserver.observe(this);
      }

      if ('IntersectionObserver' in window && this._videos.length) {
        this._videoObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio > 0.08) {
              const playPromise = video.play();
              if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
            } else {
              video.pause();
            }
          });
        }, { threshold: [0, 0.08, 0.25] });
        this._videos.forEach((video) => this._videoObserver.observe(video));
      }

      this._measure();
      this._targetProgress = this._getProgress();
      this._currentProgress = this._targetProgress;
      this._render(this._currentProgress);
      this._updateHeaderOffset();
    }

    disconnectedCallback() {
      this._connected = false;
      window.removeEventListener('scroll', this._onViewportChange);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('resize', this._updateHeaderOffset);
      window.removeEventListener('load', this._updateHeaderOffset);
      this.removeEventListener('click', this._onIntroClick);

      if (this._motionQuery) {
        if (this._motionQuery.removeEventListener) {
          this._motionQuery.removeEventListener('change', this._onMotionChange);
        } else if (this._motionQuery.removeListener) {
          this._motionQuery.removeListener(this._onMotionChange);
        }
      }

      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._videoObserver) this._videoObserver.disconnect();
      if (this._selfObserver) this._selfObserver.disconnect();
      if (this._frame) cancelAnimationFrame(this._frame);
      this._frame = 0;
    }

    _onMotionChange() {
      this._targetProgress = this._getProgress();
      this._currentProgress = this._targetProgress;
      this._render(this._currentProgress);
    }

    _onViewportChange() {
      this._targetProgress = this._getProgress();
      if (!this._frame) this._frame = requestAnimationFrame(this._tick);
    }

    // Medidas que NÃO mudam durante o scroll (só em resize/relayout): se forem lidas
    // a cada frame forçam reflow e travam o mobile. Por isso ficam em cache aqui e só
    // são atualizadas em resize e pelo ResizeObserver.
    _measure() {
      this._isMobile = window.matchMedia('(max-width: 749px)').matches;
      this._videoPanelHeight = this._videoPanel ? Math.max(this._videoPanel.offsetHeight || 0, 1) : 0;
      this._heroHeight = this._hero ? Math.max(this._hero.offsetHeight || 0, 1) : 0;
      this._logoHeight = this._logoEl ? Math.max(this._logoEl.offsetHeight || 0, 1) : 0;
    }

    _onResize() {
      this._measure();
      this._targetProgress = this._getProgress();
      if (!this._frame) this._frame = requestAnimationFrame(this._tick);
    }

    _onIntroClick(event) {
      const cue = event.target.closest('[data-intro-scroll-cue]');
      if (!cue || !this.contains(cue) || !this._hero) return;
      if (toNumber(getComputedStyle(this).getPropertyValue('--mm-scroll-cue-opacity'), 1) <= 0.05) return;

      const heroTop = this._hero.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: heroTop, behavior: 'smooth' });
    }

    // Recalcula a posição da logo depois que o layout assenta (load) ou muda (resize),
    // pois a altura/posição do header pode mudar nesses momentos.
    _updateHeaderOffset() {
      this._render(this._currentProgress);
    }

    _getProgress() {
      const rect = this.getBoundingClientRect();
      const viewportHeight = Math.max(window.innerHeight || 0, 1);
      const scrollableDistance = Math.max(rect.height - viewportHeight, 1);
      return clamp(-rect.top / scrollableDistance);
    }

    _updateMobileTrailMode(isMobile, viewportHeight) {
      this.classList.remove('mm-intro--mobile-trail');
      return false;
    }

    _tick() {
      const reduceMotion = this._motionQuery && this._motionQuery.matches;
      // No mobile a logo é posicionada a partir do scroll real (não do progresso
      // suavizado), então enquanto a seção está visível relemos o alvo a cada quadro.
      // Assim a logo acompanha o scroll por inércia de forma contínua, sem pulos.
      const liveTracking = this._inView && this._isMobile && !reduceMotion;
      if (liveTracking) this._targetProgress = this._getProgress();
      const difference = this._targetProgress - this._currentProgress;

      if (reduceMotion || Math.abs(difference) < 0.0005) {
        this._currentProgress = this._targetProgress;
      } else {
        this._currentProgress += difference * 0.16;
      }

      this._render(this._currentProgress);

      // Continua o loop enquanto: (a) o progresso ainda está suavizando, ou
      // (b) a seção está visível no mobile (pra acompanhar o scroll por inércia frame a frame).
      const stillEasing = Math.abs(this._targetProgress - this._currentProgress) >= 0.0005;
      const keepGoing = (liveTracking || stillEasing) && this.isConnected;
      if (keepGoing) {
        this._frame = requestAnimationFrame(this._tick);
      } else {
        this._frame = 0;
      }
    }

    // Grava uma custom property só quando o valor muda. Evita recalcular estilo/repaint
    // toda vez (vários valores ficam constantes durante o scroll: escala, panels-y, etc.).
    _setProp(name, value) {
      if (this._props[name] === value) return;
      this._props[name] = value;
      this.style.setProperty(name, value);
    }

    _toggleClass(name, on) {
      if (this._classes[name] === on) return;
      this._classes[name] = on;
      this.classList.toggle(name, on);
    }

    _render(progress) {
      const isMobile = this._isMobile;
      const prefix = isMobile ? 'mobile' : 'desktop';
      const reduceMotion = this._motionQuery && this._motionQuery.matches;

      // ---- LEITURAS DE LAYOUT (todas juntas, ANTES de qualquer escrita) ----
      // Ler geometria depois de escrever estilo força reflow síncrono a cada quadro.
      // Mantendo as leituras agrupadas aqui, o navegador recalcula o layout no máximo
      // uma vez por frame — é o que tira o "travadinho" do scroll no mobile.
      const viewportHeight = Math.max(window.innerHeight || 0, 1);
      const introRect = this.getBoundingClientRect();
      // A logo fica dentro do sticky. Compensamos TODO o deslocamento do sticky (positivo
      // E negativo) para a logo ficar SEMPRE na mesma posição de tela — inclusive no fim
      // da seção, quando o sticky é liberado. Assim ela NÃO sobe junto com o conteúdo nem
      // some antes da hora: fica parada na posição de repouso e só sai quando a seção
      // termina de verdade (cortada na borda da seção pelo overflow: clip).
      const stickyTop = this._sticky ? this._sticky.getBoundingClientRect().top : 0;

      // ---- CÁLCULOS (sem tocar no DOM) ----
      const parallax = reduceMotion ? 0 : toNumber(this.dataset[`${prefix}Parallax`], isMobile ? 4 : 8);
      const easedProgress = smoothstep(clamp(progress));
      const scrollCueOpacity = isMobile ? 0 : 1 - smoothstep(clamp(progress / 0.32));
      const logoOffsetTop = stickyTop;
      const logoLocked = clamp(progress) >= 0.995 && introRect.bottom > 0;

      // POSIÇÃO DE REPOUSO da logo: uma posição FIXA NA TELA (% da altura da viewport;
      // 50% = centro). A logo desce até aqui e FICA PARADA — não acompanha mais o scroll
      // depois disso, então não existe o efeito de "bater no limite e voltar" (overscroll).
      // Quando a intro termina, o sticky solta e a logo sai naturalmente com a página.
      // Desktop e mobile têm valores próprios.
      const restRatio = clamp(
        toNumber(isMobile ? this.dataset.logoRestMobile : this.dataset.logoRestDesktop, isMobile ? 60 : 58) / 100,
        0.1,
        0.95
      );
      const restY = viewportHeight * restRatio;

      // No MOBILE o vídeo é quadrado (curto): a logo desliza do centro do vídeo até a
      // posição de repouso e para. No desktop ela já fica direto na posição de repouso.
      let logoY = restY;
      if (isMobile && this._videoPanel && this._hero) {
        // Alturas vêm do cache (medidas só em resize) para não forçar reflow por quadro.
        const videoHeight = Math.max(this._videoPanelHeight || 0, 1);
        const heroHeight = Math.max(this._heroHeight || 0, viewportHeight);
        const startLogoY = videoHeight / 2;
        const travelDistance = Math.max(videoHeight + heroHeight / 2 - restY, videoHeight, 1);
        const mobileProgress = smoothstep(clamp(-introRect.top / travelDistance));
        // Ao saturar (mobileProgress = 1) o alvo vira o repouso e não muda mais, mesmo
        // com overscroll — o clamp segura no limite, então a logo fica parada de verdade.
        const rawTargetY = lerp(startLogoY, restY, mobileProgress);

        // Ease-follow: a logo persegue o alvo com atraso suave (flicks ficam graciosos).
        if (this._logoY == null || reduceMotion) {
          this._logoY = rawTargetY;
        } else {
          const delta = rawTargetY - this._logoY;
          this._logoY = Math.abs(delta) < 0.1 ? rawTargetY : this._logoY + delta * 0.1;
        }
        logoY = this._logoY;
      } else {
        // Desktop: direto na posição de repouso (sem deslize, sem lag).
        logoY = restY;
        this._logoY = restY;
      }

      // SAÍDA NATURAL no fim da seção: enquanto a seção tem folga, a logo fica fixa na
      // posição de repouso (acima). Mas, ao chegar perto do fim, em vez de ficar presa na
      // tela e ser CORTADA pela borda da seção (a logo "picotada" no meio), ela passa a
      // rolar junto com a página — ancorada um pouco acima da borda inferior da seção —
      // e sai inteira por cima, naturalmente. A margem garante que ela nunca seja cortada.
      const exitMargin = Math.max((this._logoHeight || 0) * 0.6, viewportHeight * 0.06);
      const anchoredDocY = introRect.bottom - exitMargin;
      if (anchoredDocY < logoY) logoY = anchoredDocY;

      const colorStartAt = clamp(toNumber(this.dataset.colorStartAt, 36) / 100);
      const colorEndAt = clamp(toNumber(this.dataset.colorEndAt, 64) / 100);
      const colorRange = Math.max(colorEndAt - colorStartAt, 0.001);
      const colorProgress = smoothstep(clamp((progress - colorStartAt) / colorRange));
      const logoColor = mixColor(this.dataset.logoStartColor, this.dataset.logoEndColor, colorProgress);

      const parallaxPixels = viewportHeight * parallax / 100;
      const heroShift = (easedProgress * parallaxPixels).toFixed(2);
      const videoShift = ((easedProgress - 1) * parallaxPixels * 0.55).toFixed(2);

      // ---- ESCRITAS (todas juntas, no fim, e só quando o valor mudou) ----
      this._updateMobileTrailMode(isMobile, viewportHeight);
      this._setProp('--mm-scroll-cue-opacity', scrollCueOpacity.toFixed(3));
      this._toggleClass('mm-intro--cue-hidden', scrollCueOpacity <= 0.05);
      this._toggleClass('mm-intro--logo-locked', logoLocked);
      this._setProp('--mm-mobile-panels-y', '0px');
      this._setProp('--mm-logo-y', `${(logoY - logoOffsetTop).toFixed(2)}px`);
      this._setProp('--mm-logo-scale', '1');
      this._setProp('--mm-logo-color', logoColor);
      this._setProp('--mm-hero-shift', `${heroShift}px`);
      this._setProp('--mm-video-shift', `${videoShift}px`);
    }
  }

  class MonochromeShoeStage extends HTMLElement {
    constructor() {
      super();
      this._scrollFrame = 0;
      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onBlockSelect = this._onBlockSelect.bind(this);
      this._onPointerOver = this._onPointerOver.bind(this);
      this._onScroll = this._onScroll.bind(this);
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._options = Array.from(this.querySelectorAll('[data-shoe-option]'));
      this._slides = Array.from(this.querySelectorAll('[data-shoe-slide]'));
      this._name = this.querySelector('[data-active-name]');
      this._productTitle = this.querySelector('[data-active-product]');
      this._price = this.querySelector('[data-active-price]');
      this._form = this.querySelector('[data-shoe-form]');
      this._variantInput = this.querySelector('[data-variant-input]');
      this._submit = this.querySelector('[data-shoe-submit]');
      this._link = this.querySelector('[data-shoe-link]');

      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);
      this.addEventListener('pointerover', this._onPointerOver);
      this.addEventListener('shopify:block:select', this._onBlockSelect);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });

      const initialIndex = Math.max(0, this._options.findIndex((option) => option.getAttribute('aria-selected') === 'true'));
      if (this._options.length) this.activate(initialIndex, false);
      this._updateFromScroll();
    }

    disconnectedCallback() {
      this._connected = false;
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeydown);
      this.removeEventListener('pointerover', this._onPointerOver);
      this.removeEventListener('shopify:block:select', this._onBlockSelect);
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
      if (this._scrollFrame) cancelAnimationFrame(this._scrollFrame);
      this._scrollFrame = 0;
    }

    // Hover no nome da cor já troca a cor (mouse).
    _onPointerOver(event) {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      const option = event.target.closest('[data-shoe-option]');
      if (!option || !this.contains(option)) return;
      const index = this._options.indexOf(option);
      if (index >= 0 && index !== this._activeIndex) this.activate(index, false);
    }

    _onScroll() {
      if (this._scrollFrame) return;
      this._scrollFrame = requestAnimationFrame(() => {
        this._scrollFrame = 0;
        this._updateFromScroll();
      });
    }

    // Só cicla cor no scroll quando a TRAVA (pin) está ligada. Sem trava, a cor
    // é trocada apenas por hover/clique (o scroll não mexe na cor).
    _updateFromScroll() {
      if (!this._options.length || !this.dataset.scrollPin) return;
      const rect = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const scrollable = Math.max(rect.height - viewportHeight, 1);
      let progress = -rect.top / scrollable;
      progress = Math.min(1, Math.max(0, progress));
      const index = Math.min(this._options.length - 1, Math.floor(progress * this._options.length));
      if (index !== this._activeIndex) this.activate(index, false);
    }

    _onClick(event) {
      const option = event.target.closest('[data-shoe-option]');
      if (!option || !this.contains(option)) return;
      const index = this._options.indexOf(option);
      if (index >= 0) this.activate(index, false);
    }

    _onKeydown(event) {
      const option = event.target.closest('[data-shoe-option]');
      if (!option || !this.contains(option) || !this._options.length) return;

      const currentIndex = this._options.indexOf(option);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % this._options.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + this._options.length) % this._options.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this._options.length - 1;

      if (nextIndex !== currentIndex) {
        event.preventDefault();
        this.activate(nextIndex, true);
      }
    }

    _onBlockSelect(event) {
      const wrapper = event.target.closest('[data-shoe-option-wrap]');
      if (!wrapper || !this.contains(wrapper)) return;
      const option = wrapper.querySelector('[data-shoe-option]');
      const index = this._options.indexOf(option);
      if (index >= 0) this.activate(index, false);
    }

    activate(index, focusOption = false) {
      const option = this._options[index];
      if (!option) return;
      this._activeIndex = index;

      this.style.setProperty('--mm-stage-bg', option.dataset.background || '#d7d6d1');
      this.style.setProperty('--mm-stage-text', option.dataset.textColor || '#202020');
      this.style.setProperty('--mm-stage-accent', option.dataset.accentColor || option.dataset.textColor || '#202020');

      this._options.forEach((item, itemIndex) => {
        const active = itemIndex === index;
        item.setAttribute('aria-selected', String(active));
        item.setAttribute('tabindex', active ? '0' : '-1');
      });

      this._slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });

      if (this._name) this._name.textContent = option.dataset.name || '';
      if (this._productTitle) this._productTitle.textContent = option.dataset.productTitle || '';
      if (this._price) this._price.textContent = option.dataset.price || '';

      const variantId = option.dataset.variantId || '';
      const available = option.dataset.available === 'true' && Boolean(variantId);
      const productUrl = option.dataset.productUrl || '';

      if (this._variantInput) this._variantInput.value = variantId;

      if (this._submit) {
        this._submit.disabled = !available;
        this._submit.textContent = available
          ? (this._submit.dataset.availableLabel || 'Adicionar ao carrinho')
          : (this._submit.dataset.soldOutLabel || 'Indisponível');
      }

      if (this._link) {
        if (productUrl) {
          this._link.href = productUrl;
          this._link.removeAttribute('aria-disabled');
          this._link.removeAttribute('tabindex');
        } else {
          this._link.removeAttribute('href');
          this._link.setAttribute('aria-disabled', 'true');
          this._link.setAttribute('tabindex', '-1');
        }
      }

      if (focusOption) option.focus({ preventScroll: true });
    }
  }

  if (!customElements.get('monochrome-scroll-intro')) {
    customElements.define('monochrome-scroll-intro', MonochromeScrollIntro);
  }

  if (!customElements.get('monochrome-shoe-stage')) {
    customElements.define('monochrome-shoe-stage', MonochromeShoeStage);
  }

  if (!window.__monochromeMediaPanelVideo) {
    window.__monochromeMediaPanelVideo = true;
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-media-play]');
      if (!button) return;

      const frame = button.closest('[data-media-video-frame]');
      if (!frame) return;

      const videos = Array.from(frame.querySelectorAll('video'));
      const video = videos.find((item) => item.offsetParent !== null) || videos[0];
      if (!video) return;

      video.controls = true;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => frame.classList.add('is-playing'))
          .catch(() => {
            video.controls = false;
          });
      } else {
        frame.classList.add('is-playing');
      }
    });
  }
})();
